import React from "react";
import { Link } from "react-router-dom";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ArrowRight, ClipboardCheck, CalendarCheck } from "lucide-react";
import PoweredBy from "@/components/PoweredBy";
import RefundCalculator from "@/components/RefundCalculator";
import WelcomeVideoHero from "@/components/WelcomeVideoHero";
import WelcomeMessage from "@/components/WelcomeMessage";
import IntroSplash from "@/components/IntroSplash";

export default function Home() {
  const { branding } = useBranding();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background paper-texture">
      <IntroSplash />
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="home-brand-link">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-3 sm:gap-4 text-sm">
            <Link to="/intake" className="hidden sm:inline underline text-muted-foreground hover:text-foreground font-medium" data-testid="home-nav-intake">Intake form</Link>
            <Link to="/book" className="hidden md:inline underline text-muted-foreground hover:text-foreground font-medium" data-testid="home-nav-book">Book</Link>
            <Link to="/tax-pros" className="hidden md:inline underline text-accent hover:text-accent/80 font-medium" data-testid="home-nav-taxpros">For Tax Pros</Link>
            <Link to="/meet" className="hidden lg:inline underline text-muted-foreground hover:text-foreground" data-testid="home-nav-meet">Meet me</Link>
            {user ? (
              <Button size="sm" onClick={() => window.location.assign(user.role === "admin" ? "/admin" : "/dashboard")} data-testid="home-nav-dashboard">Go to app</Button>
            ) : (
              <>
                <Link to="/login" className="underline text-foreground" data-testid="home-nav-login">Sign in</Link>
                <Button size="sm" asChild data-testid="home-nav-register"><Link to="/register">Get started</Link></Button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Welcome video hero — hidden until you upload one from /admin */}
      <WelcomeVideoHero />

      {/* Welcome message — client-focused copy + two CTAs (intake / book) */}
      <WelcomeMessage />

      {/* Refund calculator — inline, right on the welcome page.
          Client-side only: TY 2025 federal estimate. No API calls needed. */}
      <section className="border-t border-border bg-card" data-testid="home-refund-section">
        <div className="max-w-6xl mx-auto p-6 lg:p-12 py-16">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-2">
              <div className="label-caps mb-3 text-primary">Step 1 · Estimate</div>
              <h2 className="font-serif text-4xl md:text-5xl mb-4 leading-tight">
                See what you could get back — <span className="italic text-primary">right here.</span>
              </h2>
              <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                A quick TY 2025 federal estimate. Plug in your W-2 wages, withholding, and any dependents — the calculator handles standard deduction, Child Tax Credit (with the refundable ACTC), and Earned Income Tax Credit.
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Ready for the real number? Start your intake or book an appointment below.
              </p>
            </div>
            <div className="lg:col-span-3">
              <RefundCalculator />
            </div>
          </div>
        </div>
      </section>

      {/* Intake + Book — the next two steps, big and obvious */}
      <section className="border-t border-border" data-testid="home-actions-section">
        <div className="max-w-6xl mx-auto p-6 lg:p-12 py-16">
          <div className="grid md:grid-cols-2 gap-6">
            <ActionCard
              step="Step 2"
              icon={<ClipboardCheck size={28} />}
              title="Client Intake Form"
              body="Tell us about your household, income, deductions, and upload your paperwork (W-2s, 1099s, IDs). Takes about 10 minutes — save & finish on any device."
              cta="Start intake form"
              to="/intake"
              testid="home-action-intake"
              highlight
            />
            <ActionCard
              step="Step 3"
              icon={<CalendarCheck size={28} />}
              title="Book Your Appointment"
              body="Pick a video slot that works for you. We'll walk through your return live, answer questions, and e-file the same session."
              cta="See available times"
              to="/book"
              testid="home-action-book"
            />
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
          <PoweredBy className="!m-0" />
          <div className="flex gap-4 flex-wrap">
            <Link to="/intake" className="underline hover:text-foreground">Intake</Link>
            <Link to="/book" className="underline hover:text-foreground">Book</Link>
            <Link to="/client-portal" className="underline hover:text-foreground">Client portal</Link>
            <Link to="/tax-pros" className="underline hover:text-foreground">For Tax Pros</Link>
            <Link to="/become-a-tax-pro" className="underline hover:text-foreground">Become a Tax Pro</Link>
            <Link to="/meet" className="underline hover:text-foreground">Meet your preparer</Link>
            <Link to="/login" className="underline hover:text-foreground">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ActionCard({ step, icon, title, body, cta, to, testid, highlight }) {
  return (
    <div
      className={`p-6 lg:p-8 rounded-lg border-2 ${highlight ? "border-primary bg-primary/5" : "border-border bg-card"} flex flex-col`}
      data-testid={testid}
    >
      <div className={`h-14 w-14 rounded-md ${highlight ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"} grid place-items-center mb-4`}>
        {icon}
      </div>
      <div className="label-caps mb-1 text-primary">{step}</div>
      <h3 className="font-serif text-2xl mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-5">{body}</p>
      <Button asChild variant={highlight ? "default" : "outline"} size="lg" className="w-full sm:w-auto" data-testid={`${testid}-btn`}>
        <Link to={to}>{cta} <ArrowRight size={14} className="ml-2" /></Link>
      </Button>
    </div>
  );
}
